import _ from 'lodash';
import { IProjectJson } from '@scale/shared/types/project';
import { supportsDocumentTooling } from '@scale/shared/document/util';
import { MAX_NEW_USER_PENDING_ATTEMPTS, SubtaskServingStrategy } from '@scale/shared/defaults';

// types
export interface ISection {
  title: string;
  parent:
    | 'General'
    | 'Grader'
    | 'Benchmarks'
    | 'Promoting'
    | 'Disabling'
    | 'Linting'
    | 'Guided Workflow'
    | 'Prebuild'
    | 'Task Holding'
    | 'Claim'
    | 'Pause Timer'
    | 'Throttle'
    | 'Benchmark Throttle'
    | 'Autoremove Bad Taskers'
    | 'Task Redo'
    | 'Super Collectors'
    | 'Text Collection'
    | 'Quality First Queue';
  // feel free to add
  description: string;
  children: Array<IConfig | Array<IConfig>>;
}

export interface IBaseConfig {
  _type: string;
  value: any;
  displayLabel?: string;
  default?: any;
  leaf?: boolean;
  source?: string;
  enumValues?: Array<string>;
  [key: string]: any; // allow for the addition of name & path
}

export interface IConfig extends IBaseConfig {
  name: string;
  path: string;
  partitions?: Array<Record<string, IBaseConfig> | null>;
  children?: Record<string, IConfig>;
  arrayIndex?: number;
}

// given paths, create array of config objects
const pathsToChildren = (
  paths: Array<string | Array<string>>,
  config: Record<string, any>,
): Array<IConfig | Array<IConfig>> => {
  const prefix = 'configSettingsValues.'; // where all flattened configs are (for now)
  const childrenArray: Array<IConfig | Array<IConfig>> = [];

  paths.forEach(path => {
    if (!_.isArray(path)) {
      const configObj = _.get(config, prefix + path);
      if (configObj) {
        configObj.name = path.substring(path.lastIndexOf('.') + 1);
        configObj.path = prefix + path;
        childrenArray.push(configObj);
      }
    } else {
      const partitions: IConfig[] = [];
      path.forEach(partition => {
        const configObj = _.get(config, prefix + partition);
        if (configObj) {
          configObj.name = partition.substring(partition.lastIndexOf('.') + 1);
          configObj.path = prefix + partition;
          partitions.push(configObj);
        }
      });

      childrenArray.push(partitions);
    }
  });

  return childrenArray;
};

export const createSections = (
  config: Record<string, any>,
  project?: IProjectJson | null,
): Array<ISection> => {
  const allSections = [];

  const productVertical: ISection = {
    title: 'Product Vertical',
    parent: 'General',
    description: 'The product vertical that owns this project.',
    children: pathsToChildren(['productVertical'], config),
  };
  allSections.push(productVertical);

  const subtaskServingStrategy: ISection = {
    title: 'Subtask Serving Strategy',
    parent: 'General',
    description:
      'The strategy for selecting which subtask the queue serves to taskers first. Default is by the earliest due date.',
    children: pathsToChildren(['subtaskServingStrategy'], config),
  };
  allSections.push(subtaskServingStrategy);

  const reviewerSpecializationFields: ISection = {
    title: 'Reviewer Specializations Subtask Serving Fields',
    parent: 'General',
    description:
      'Text collection category field IDs for setting subtask specializations when serving subtasks according to reviewer specializations. If your project uses task templates, you can get find the relevant field IDs from the task templates editor.',
    children: pathsToChildren(['reviewerSpecializationFields'], config),
  };
  if (
    config.configSettingsValues.subtaskServingStrategy.value ===
      SubtaskServingStrategy.ReviewerSpecialization ||
    config.configSettingsValues.subtaskServingStrategy.value ===
      SubtaskServingStrategy.ReviewerSpecializationLatest
  ) {
    allSections.push(reviewerSpecializationFields);
  }

  const cheatingDetectionExcludedFields: ISection = {
    title: 'Cheating Detection Excluded Fields',
    parent: 'General',
    description: 'Comma separated list of fields to exclude from cheating detection heuristics.',
    children: pathsToChildren(['cheatingDetectionExcludedFields'], config),
  };
  allSections.push(cheatingDetectionExcludedFields);

  const specializedRoutingPreventFallback: ISection = {
    title: 'Specialized Routing Prevent Fallback to Unmatched Specializations',
    parent: 'General',
    description:
      'If using the specialized routing serving strategy, enable this config to prevent falling back to serving a subtask or task that does NOT match specializations. By default, the queue will try to match specializations but fall back on serving any subtask.',
    children: pathsToChildren(['specializedRoutingPreventFallback'], config),
  };
  if (
    config.configSettingsValues.subtaskServingStrategy.value ===
      SubtaskServingStrategy.ReviewerSpecialization ||
    config.configSettingsValues.subtaskServingStrategy.value ===
      SubtaskServingStrategy.ReviewerSpecializationLatest
  ) {
    allSections.push(specializedRoutingPreventFallback);
  }

  const inlineComments: ISection = {
    title: 'Inline Comments',
    parent: 'General',
    description:
      'The section of inline comments related settings. Inline comments allow for spatially placed comments in 2D, 3D, and Doc.',
    children: pathsToChildren(['inlineCommentsFromReview'], config),
  };
  allSections.push(inlineComments);

  const zeroReview: ISection = {
    title: 'Zero Review',
    parent: 'General',
    description:
      'This section of zero review related settings. Zero review allows allow some taskers (super attempters) to submit tasks without reviews. check https://docs.google.com/document/d/1lPnOMHsYmEMMU9TtOtgfvWqP3Qk-WJWHxDgKmNvHayg/edit#',
    children: pathsToChildren([['enableZeroReview'], ['enableSpotterInlineComments']], config),
  };
  allSections.push(zeroReview);

  const imageInfo: ISection = {
    title: 'Image Info',
    parent: 'General',
    description:
      'This section of image info related settings. This text will appear as info boxes on top of their respective images.',
    children: pathsToChildren([['mainFrameInfo'], ['contextFrameInfo']], config),
  };
  allSections.push(imageInfo);

  const priorityLabels: ISection = {
    title: 'Priority Labels',
    parent: 'General',
    description:
      'The section of priority label related settings. Priority labels will be highlighted for taskers to select.',
    children: pathsToChildren(['priorityLabels'], config),
  };
  allSections.push(priorityLabels);

  const choiceDisplayMap: ISection = {
    title: 'Choice to Display Map',
    parent: 'General',
    description:
      'The section of choice label map related settings. The map will determine which labels to render to the labeler.',
    children: pathsToChildren(['choiceDisplayMap'], config),
  };
  allSections.push(choiceDisplayMap);

  const colorDefault: ISection = {
    title: 'Color Default',
    parent: 'General',
    description:
      'The section of color by default related settings. The color by default determines what field is used to determine the annotations color.',
    children: pathsToChildren(['colorDefault'], config),
  };
  allSections.push(colorDefault);

  const alertingConfigs: ISection = {
    title: 'Alerting Configs',
    parent: 'General',
    description: 'These are configs to do with various alerting mechanisms.',
    children: pathsToChildren(
      [
        // cost initiative alerts
        ['longTailSubtaskAttemptCutoff', 'depermissionWageCutoff'],
      ],
      config,
    ),
  };
  allSections.push(alertingConfigs);

  const sensitiveContentReportingConfigs: ISection = {
    title: 'Sensitive Content Reporting Configs',
    parent: 'General',
    description: 'Configs related to sensitive content reporting.',
    children: pathsToChildren(
      [
        'sensitiveContentReportingEnabledForAttempt',
        'sensitiveContentReportingEnabledForReview',
        'highRiskSensitiveContentReportingEnabledForAttempt',
        'highRiskSensitiveContentReportingEnabledForReview',
        'registerTaskAttemptsEnabledForSensitiveContentReporting',
      ],
      config,
    ),
  };
  allSections.push(sensitiveContentReportingConfigs);

  const surveyConfigs: ISection = {
    title: 'Survey Configs',
    parent: 'General',
    description: 'Configs related to surveys.',
    children: pathsToChildren(['surveyId', 'surveyFrequencyInDays', 'surveyStartDate'], config),
  };
  allSections.push(surveyConfigs);

  const textCollectionInsightsDashboardConfigs: ISection = {
    title: '[TC] Insights Dashboard Configs',
    parent: 'Text Collection',
    description: `Configs related to enabling the customer facing insights dashboard. The dashboard is customizable, according to the documentation here: https://scale.atlassian.net/l/cp/QBQTxb0r.`,
    children: pathsToChildren(
      ['textCollectionInsightsDashboardEnabled', 'textCollectionInsightsDashboardInactive'],
      config,
    ),
  };
  allSections.push(textCollectionInsightsDashboardConfigs);

  const textCollectionTaxonomyManagementConfigs: ISection = {
    title: '[TC] Taxonomy Management Configs',
    parent: 'Text Collection',
    description: `Enabling the text collection taxonomy management UI. See documentation here: https://scale.atlassian.net/wiki/spaces/OP/pages/289308760/Taxonomy+Management`,
    children: pathsToChildren(['textCollectionTaxonomyManagementEnabled'], config),
  };
  allSections.push(textCollectionTaxonomyManagementConfigs);

  const perAnnotationAuditUi: ISection = {
    title: 'Per Annotation Audit/Fixless Audits UI',
    parent: 'General',
    description: 'Whether to use the per annotation audit UI for customer audits.',
    children: pathsToChildren(
      [
        'usePerAnnotationAuditUi',
        'enableFixedPerAnnotationErrors',
        'perAnnotationAuditErrorTypes',
        'perAnnotationAuditSeverity',
      ],
      config,
    ),
  };
  allSections.push(perAnnotationAuditUi);

  const showCustomerAuditDiffs: ISection = {
    title: 'Show Customer Audit Diffs',
    parent: 'General',
    description: 'Whether to show customer audit diffs in the customer audit UI.',
    children: pathsToChildren(['showCustomerAuditDiffs'], config),
  };
  allSections.push(showCustomerAuditDiffs);

  const assignRejections: ISection = {
    title: 'Assign Rejections',
    parent: 'General',
    description: 'Assign rejected tasks to the original attempter',
    children: pathsToChildren(['assignRejections'], config),
  };
  allSections.push(assignRejections);

  const rejectionSameAssignCount: ISection = {
    title: 'Assign rejections count',
    parent: 'General',
    description: 'Number of Times to assign rejections to original attempter',
    children: pathsToChildren(['rejectionSameAssignCount'], config),
  };
  allSections.push(rejectionSameAssignCount);

  const projectPlatform: ISection = {
    title: 'Assign project platform',
    parent: 'General',
    description: 'The platform the project is completed on',
    children: pathsToChildren(['projectPlatform'], config),
  };
  allSections.push(projectPlatform);

  const disableFromWorkerPools: ISection = {
    title: 'Disable from worker pools',
    parent: 'General',
    description:
      'Toggle on to prevent the project from being assigned to qualified workers on other teams who run out of tasks for their immediately assigned projects.',
    children: pathsToChildren(['disableFromWorkerPools'], config),
  };
  allSections.push(disableFromWorkerPools);

  const lidarTaskProcessingConfigs: ISection = {
    title: 'Lidar Task Processing Settings',
    parent: 'General',
    description: 'Additional flags for lidar task processing',
    children: pathsToChildren(['bpcEncodingEnabled'], config),
  };
  allSections.push(lidarTaskProcessingConfigs);

  const autoSaveWorkToS3: ISection = {
    title: '[TC] Auto-Save Work to Remote',
    parent: 'Text Collection',
    description: 'Special toggle for remote caching for text collection',
    children: pathsToChildren(['autoSaveWorkToS3'], config),
  };
  allSections.push(autoSaveWorkToS3);

  const codingWorkspaceConfigs: ISection = {
    title: 'Coding Workspace Configs',
    parent: 'Text Collection',
    description: 'Configs for coding workspace fields',
    children: pathsToChildren(['codingWorkspaceImageVersion'], config),
  };
  allSections.push(codingWorkspaceConfigs);

  const textCollectionCategoryFieldsConfigs: ISection = {
    title: '[TC] Category Field Configs',
    parent: 'Text Collection',
    description: 'Category Field Configs for Text Collection Projects (Tasking UX)',
    children: pathsToChildren(
      [
        'textCollectionCanSelectIntermediateNodes',
        'textCollectionDisableIntermediateAutoSelect',
        'textCollectionPersistSearchQueryAfterSelect',
        'textCollectionGroupSameLabelChoices',
        'textCollectionStyleSelectedChoicesBorders',
      ],
      config,
    ),
  };
  allSections.push(textCollectionCategoryFieldsConfigs);

  const textCollectionTextFieldsConfigs: ISection = {
    title: '[TC] Text Field Configs',
    parent: 'Text Collection',
    description: 'Text Field Configs for Text Collection Projects (Tasking UX)',
    children: pathsToChildren(
      ['textCollectionShowPreviousResponsesForTextFields', 'textCollectionUseRawImageSize'],
      config,
    ),
  };
  allSections.push(textCollectionTextFieldsConfigs);

  const textCollectionInteractionClicking: ISection = {
    title: '[TC/NER] Interaction Clicking',
    parent: 'Linting',
    description: 'Tracking tasker interaction with text attachments',
    children: pathsToChildren(['textCollectionInnerTextLinksToRecordClicks'], config),
  };
  allSections.push(textCollectionInteractionClicking);

  const textCollectionSemanticSearch: ISection = {
    title: '[TC] Semantic Search (Category Fields)',
    parent: 'Text Collection',
    description:
      'Semantic search populates non-string-match results when searching in large nested taxonomies',
    children: pathsToChildren(
      ['textCollectionUseSemanticSearch', 'textCollectionEmbeddingsLink'],
      config,
    ),
  };
  allSections.push(textCollectionSemanticSearch);

  const textCollectionSmartAssistant: ISection = {
    title: '[TC] Smart Assistant',
    parent: 'Text Collection',
    description: 'Smart Assistant for Text Collection Projects',
    children: pathsToChildren(
      [
        'textCollectionSmartAssistantReviewLevels',
        'textCollectionSmartAssistantPrompt',
        'textCollectionSmartAssistantInstructionsS3Document',
      ],
      config,
    ),
  };
  allSections.push(textCollectionSmartAssistant);

  const textCollectionRandomizeAttachmentOrder: ISection = {
    title: '[TC] Randomize Attachment Order',
    parent: 'Text Collection',
    description: 'Randomize text collection attachment order when multiple attachments exist',
    children: pathsToChildren(
      ['textCollectionRandomizeAttachmentOrder', 'textCollectionRandomizeAttachmentOrderIndices'],
      config,
    ),
  };
  allSections.push(textCollectionRandomizeAttachmentOrder);

  const benchmarkReporting: ISection = {
    title: '[TC] Benchmark Reporting',
    parent: 'Text Collection',
    description:
      'Prevent taskers (at specific review levels) from reporting inaccurate instructive benchmarks to corp',
    children: pathsToChildren(
      [
        'tcBenchmarkReportingDisabledNeg1',
        'tcBenchmarkReportingDisabled0',
        'tcBenchmarkReportingDisabled1',
        'tcBenchmarkReportingDisabled10',
        'tcBenchmarkReportingDisabled11',
        'tcBenchmarkReportingDisabled12',
      ],
      config,
    ),
  };
  allSections.push(benchmarkReporting);

  const textCollectionAuditWorkflowConfigs: ISection = {
    title: '[TC] Audit Workflow Configs',
    parent: 'Text Collection',
    description: 'Audit Workflow Configs for Text Collection Projects',
    children: pathsToChildren(['textCollectionAcceptableAuditWorkflow'], config),
  };
  allSections.push(textCollectionAuditWorkflowConfigs);

  const graderMatcherParams: ISection = {
    title: 'Grader Matcher',
    parent: 'Grader',
    description:
      'Configurations for the grader matcher, which determines how to pair annotations before grading them.',
    children: pathsToChildren(['graderMatcherParams'], config),
  };
  allSections.push(graderMatcherParams);

  const graderTransformerParams: ISection = {
    title: 'Grader Transformer',
    parent: 'Grader',
    description:
      'Configurations for the grader transformer, which determines how to transform responses into gradeable annotations.',
    children: pathsToChildren(['graderTransformerParams'], config),
  };
  allSections.push(graderTransformerParams);

  const graderContextParams: ISection = {
    title: 'Grader Context Params',
    parent: 'Grader',
    description:
      'Configurations for the grader context, which determines whether attempter labels are equivalent to ground truth labels. Currently mostly used in Federal - see https://docs.google.com/document/d/11Nv4VPwUYe_QmIMA66DtXsluphvh6VAG_CrrLM0T1u8 for details.',
    children: pathsToChildren(['graderContextParams'], config),
  };
  allSections.push(graderContextParams);

  const benchmarkServing: ISection = {
    title: 'Benchmark serving',
    parent: 'Benchmarks',
    description:
      'Benchmarks (BMs) are fake tasks that automatically evaluate a tasker’s work. Benchmarks are used as a tool to sample the quality of work of individual taskers and, in aggregate, a queue. This set of configs informs some basic settings related to how benchmarks get served, such as frequency.',
    children: pathsToChildren(
      [
        [
          'benchmarkRateAttempt',
          'benchmarkRateL0',
          'benchmarkRateL1',
          'benchmarkRateReviewConsensus',
          'benchmarkRateL10',
          'benchmarkRateQIRAuditor',
          'benchmarkRateQIRLeadAuditor',
        ],
        [
          'decayedBenchmarkWallSizeAttempt',
          'decayedBenchmarkWallSizeL0',
          'decayedBenchmarkWallSizeL1',
          'decayedBenchmarkWallSizeL4',
          'decayedBenchmarkWallSizeL10',
        ],
        ['onlyServeWallBenchmarksWithinWall'],
        ['passBenchmarkWallOnConsecutivePassedBenchmarksAttempt'],
        ['wallBenchmarkPassThresholdAttempt'],
        ['canaryBenchmarksConfig'],
        ['serveReviewBenchmarksToAttempters'],
        'percentInitialInstructiveBenchmarks',
        'instructiveBenchmarkRate',
        'balancedByInstructionItem',
        'benchmarkServeTraineesBenchmarks',
      ],
      config,
    ),
  };
  allSections.push(benchmarkServing);

  const queueAccess: ISection = {
    title: 'Benchmarks: initial wall',
    parent: 'Benchmarks',
    description: `You can require taskers complete some number of benchmarks before they start doing actual work on a queue. Will only take effect if the corresponding benchmark serving rate > 0!

      WARNING: This should be <= 200 due to TME dataBuffers limits`,
    children: pathsToChildren(
      [
        [
          'minimumBenchmarksAttempt',
          'minimumBenchmarksL0',
          'minimumBenchmarksL1',
          'minimumBenchmarksReviewConsensus',
          'minimumBenchmarksL10',
        ],
        [
          'minimumBenchmarksPerInstructionItemAttempt',
          'minimumBenchmarksPerInstructionItemL0',
          'minimumBenchmarksPerInstructionItemL1',
          'minimumBenchmarksPerInstructionItemL10',
          'ignorePreviouslyCompletedBenchmarksForBenchmarkWallAfterRebirth',
        ],
        'skipBenchmarkWallWorkerTags',
      ],
      config,
    ),
  };
  allSections.push(queueAccess);

  const benchmarkExhaustion: ISection = {
    title: 'Benchmark exhaustion',
    parent: 'Benchmarks',
    description:
      "Since every project has some finite number of benchmarks, it's possible that a particular tasker can (over time) complete all benchmarks on a given project. This set of configs determines what should happen in that scenario.",
    children: pathsToChildren(
      [
        'shouldReuseBenchmarks',
        [
          'shouldThrottleOnBenchmarkExhaustionAttempt',
          'shouldThrottleOnBenchmarkExhaustionL0',
          'shouldThrottleOnBenchmarkExhaustionL1',
          'shouldThrottleOnBenchmarkExhaustionL10',
        ],
      ],
      config,
    ),
  };
  allSections.push(benchmarkExhaustion);

  const benchmarkPayouts: ISection = {
    title: 'Benchmark payout overrides',
    parent: 'Benchmarks',
    description:
      'These configs override the default pay settings for the project. If you know more, ping Sophia about contributing to Nice Configs.',
    children: pathsToChildren(
      [
        'benchmarkPayoutsEnabled',
        'benchmarkPayoutsAccuracyTarget',
        'benchmarkPayoutsSpeedTarget',
        'benchmarkPayoutsRewardAmountHundredthsOfCents',
        'benchmarkPayoutsPayoutWindowSize',
        'enableAccuracyBuckets',
      ],
      config,
    ),
  };
  allSections.push(benchmarkPayouts);

  const benchmarkMiscellaneous: ISection = {
    title: 'Benchmarks: miscellaneous',
    parent: 'Benchmarks',
    description: "Random benchmark configs that didn't really fit anywhere else.",
    children: pathsToChildren(
      ['autoBenchmarkFromAudit', 'showBenchmarkAccuracyBar', 'autocreateBenchmarkFromReview'],
      config,
    ),
  };
  allSections.push(benchmarkMiscellaneous);

  const autoPromoteBenchmark: ISection = {
    title: 'Auto Promoting: attempter benchmarks',
    parent: 'Promoting',
    description:
      'Determines if taskers should be auto promoted based on an attempter benchmark score threshold. Threshold and min task count must also be set for auto promotion logic to run.',
    children: pathsToChildren(
      [
        'autoPromoteBenchmark',
        'autoPromoteBenchmarkAccuracyThreshold',
        'autoPromoteBenchmarkMinTaskCount',
      ],
      config,
    ),
  };
  allSections.push(autoPromoteBenchmark);

  const disablingBenchmarkAccuracyThreshold: ISection = {
    title: 'Disabling: attempter benchmarks',
    parent: 'Disabling',
    description:
      'Determines how attempters get disabled from a project. We need high quality work to be done on a queue in order to deliver high quality data to customers, so we can disable (or warn) taskers who are doing low quality work (as measured by benchmarks). ' +
      "Disabling (& warning) is performed using accuracy from the tasker's last X benchmark attempts, with X defined in 'Min benchmark count' field",
    children: pathsToChildren(
      [
        'disablingBenchmarkAccuracyThresholdWarnAttempt',
        'disablingBenchmarkAccuracyThresholdDisableAttempt',
        // deprecated
        // 'disablingBenchmarkAccuracyThresholdMinWarnTaskCountAttempt',
        // 'disablingBenchmarkAccuracyThresholdMinDisableTaskCountAttempt',
        'disablingBenchmarkAccuracyThresholdMinTaskCountAttempt',
        'disableWarnBasedOnEnabledBenchmarksOnlyAttempt',
        'disableWarnBasedOnEvaluativeBenchmarksOnlyAttempt',
      ],
      config,
    ),
  };
  allSections.push(disablingBenchmarkAccuracyThreshold);

  const disablingReviewerAccuracyThreshold: ISection = {
    title: 'Disabling: reviewer benchmarks',
    parent: 'Disabling',
    description:
      'Determines how reviewers get disabled from a project. We need high quality work to be done on a queue in order to deliver high quality data to customers, so we can disable (or warn) taskers who are doing low quality work (as measured by benchmarks).' +
      "Disabling is performed using accuracy from the tasker's last X benchmark reviews, with X defined in 'L*: min benchmark count' fields",
    children: pathsToChildren(
      [
        [
          'disablingBenchmarkAccuracyThresholdWarnL0',
          'disablingBenchmarkAccuracyThresholdWarnL1',
          'disablingBenchmarkAccuracyThresholdWarnReviewConsensus',
          'disablingBenchmarkAccuracyThresholdWarnL10',
        ],
        [
          'disablingBenchmarkAccuracyThresholdDisableL0',
          'disablingBenchmarkAccuracyThresholdDisableL1',
          'disablingBenchmarkAccuracyThresholdDisableReviewConsensus',
          'disablingBenchmarkAccuracyThresholdDisableL10',
        ],
        // deprecated
        // [
        //   'disablingBenchmarkAccuracyThresholdMinWarnTaskCountL0',
        //   'disablingBenchmarkAccuracyThresholdMinWarnTaskCountL1',
        //   'disablingBenchmarkAccuracyThresholdMinWarnTaskCountL10',
        // ],
        // [
        //   'disablingBenchmarkAccuracyThresholdMinDisableTaskCountL0',
        //   'disablingBenchmarkAccuracyThresholdMinDisableTaskCountL1',
        //   'disablingBenchmarkAccuracyThresholdMinDisableTaskCountL10',
        // ],
        [
          'disablingBenchmarkAccuracyThresholdMinTaskCountL0',
          'disablingBenchmarkAccuracyThresholdMinTaskCountL1',
          'disablingBenchmarkAccuracyThresholdMinTaskCountReviewConsensus',
          'disablingBenchmarkAccuracyThresholdMinTaskCountL10',
        ],
        [
          'disableWarnBasedOnEnabledBenchmarksOnlyReview',
          'disableWarnBasedOnEvaluativeBenchmarksOnlyReview',
        ],
        ['disableDirectlyFromReview'],
      ],
      config,
    ),
  };
  allSections.push(disablingReviewerAccuracyThreshold);

  const disablingAttempterAccuracyThreshold: ISection = {
    title: 'Disabling: non-benchmark',
    parent: 'Disabling',
    description:
      'You can also disable/warn taskers based on their accuracy on "normal" tasks. Accuracy here is determined by the % of the task the attempter got correct when compared to the final submitted response.',
    children: pathsToChildren(
      [
        'attempterAccuracyThresholdWarn',
        'attempterAccuracyThresholdDisable',
        'attempterAccuracyThresholdPromote',
        'attempterAccuracyThresholdMinWarnTaskCount',
        'attempterAccuracyThresholdMinDisableTaskCount',
        'attempterAccuracyThresholdMinPromoteTaskCount',
      ],
      config,
    ),
  };
  allSections.push(disablingAttempterAccuracyThreshold);

  const disablingInstructionItem: ISection = {
    title: 'Disabling: instruction items',
    parent: 'Disabling',
    description: 'Disable/warn taskers based on their accuracy on instruction items.',
    children: pathsToChildren(['disablingBenchmarkAccuracyInstructionItemThresholds'], config),
  };
  allSections.push(disablingInstructionItem);

  const softDisabling: ISection = {
    title: 'Soft disabling',
    parent: 'Disabling',
    description:
      'Taskers enter "soft disabling" when their accuracy has gone below a certain threshold. At this point, they only receive benchmarks (potentially focused on the error that they’re performing poorly on) and have up to some X number of benchmarks to bring their accuracy back above the threshold, or be permanently disabled.',
    children: pathsToChildren(
      [
        'softDisablingConfigEnabled',
        'disablingBenchmarkAccuracyThresholdSoftDisableAttempt',
        'softDisablingConfigMaxBenchmarks',
        'softDisablingConfigInstructiveBenchmarkRate',
        'softDisablingConfigCourses',
        'softDisablingConfigMaxCount',
      ],
      config,
    ),
  };
  allSections.push(softDisabling);

  const responseLinter: ISection = {
    title: 'Response linter',
    parent: 'Disabling',
    description:
      'Set up custom scripts to run on this project. See corp/linter_config_docs for detailed linter documentation.',
    children: pathsToChildren(['responseLinterConfigRules'], config),
  };
  allSections.push(responseLinter);

  if (supportsDocumentTooling(project?.taskType)) {
    const mlLinter: ISection = {
      title: 'ML Fast Linter',
      parent: 'Linting',
      description: 'Allow for ML linters to run on this project.',
      children: pathsToChildren(['mlFastLinterConfigs'], config),
    };
    allSections.push(mlLinter);
  }

  const disablingNLPSpecific: ISection = {
    title: 'Disabling: C&L-specific',
    parent: 'Disabling',
    description:
      'C&L is special so it gets special disabling configs. Specifically, there are categorization tasks where we want to set thresholds for specific answer choices. We also have a few different graders to choose from.',
    children: pathsToChildren(
      ['benchmarkChoiceThresholds', 'benchmarkTaskGrader', 'benchmarkReviewGrader'],
      config,
    ),
  };
  allSections.push(disablingNLPSpecific);

  const disablingMiscellaneous: ISection = {
    title: 'Disabling: miscellaneous',
    parent: 'Disabling',
    description: `Random disabling configs that didn't really fit anywhere else. NOTE: if maxNewUserPendingAttempts is not set, in code we default to ${MAX_NEW_USER_PENDING_ATTEMPTS}`,
    children: pathsToChildren(
      [
        'maxNewUserPendingAttempts',
        'showBenchmarkPassRate',
        'dailySkipLimit',
        'allowLidarSkipsAtAttempt',
        'dailyBenchmarkSkipLimit',
      ],
      config,
    ),
  };
  allSections.push(disablingMiscellaneous);

  const multitouchConfigs: ISection = {
    title: 'General: Multitouch',
    parent: 'General',
    description: `Modify behavior for multitouch projects/taskers`,
    children: pathsToChildren(['bypassMultitouch'], config),
  };
  allSections.push(multitouchConfigs);

  const taskWallThrottling: ISection = {
    title: 'Throttling: Task Wall',
    parent: 'Throttle',
    description: `Throttle new taskers with a production task wall (taskers must pass N production tasks within M allowed attempts each). See confluence docs for details: https://scale.atlassian.net/wiki/spaces/OP/pages/679575634/Task+Wall+Throttle`,
    children: pathsToChildren(
      [
        'taskWallTaskCount',
        'taskWallAllowedAttemptsPerTask',
        'taskWallRequireAllTasksPassed',
        'taskWallAllowAttemptsAtReviewLevels',
        'taskWallSkipWorkerTags',
      ],
      config,
    ),
  };
  allSections.push(taskWallThrottling);

  const automateMetricsFilters: ISection = {
    title: 'Automate Metrics Filters',
    parent: 'General',
    description:
      'Run every 30 minutes to automatically filter out workers and attempts based on different sets of metrics.',
    children: pathsToChildren(['automateMetricsFilters'], config),
  };
  allSections.push(automateMetricsFilters);

  const guidedWorkflowDefinitions: ISection = {
    title: 'Definitions',
    parent: 'Guided Workflow',
    description:
      'Set up descriptive workflows to show taskers on this project. Primarily for GIS & some 2D tasks types',
    children: pathsToChildren(['guidedWorkflowDefinitionConfigRules'], config),
  };
  allSections.push(guidedWorkflowDefinitions);

  const internalInstruction: ISection = {
    title: 'Internal Instructions',
    parent: 'General',
    description:
      'Internal Instructions work like regular instructions, but are hidden to the customer and stored at the subtask level.',
    children: pathsToChildren(['internalInstruction'], config),
  };
  allSections.push(internalInstruction);

  const internalFields: ISection = {
    title: '[TC] Internal Fields',
    parent: 'Text Collection',
    description: `Tasker-facing fields that aren't shared with the customer. Usually used to collect extra data for use in consensus/benchmarks.`,
    children: pathsToChildren(['internalFields'], config),
  };
  allSections.push(internalFields);

  const prebuild: ISection = {
    title: 'Prebuild configs',
    parent: 'Prebuild',
    description:
      "The prebuild stage can be used before launching a real project with actual customer tasks. You can identify quality taskers by uploading golden dataset tasks (tasks with answer choices, usually created by the customer) and benchmarks. By identifying these quality taskers early, you can quickly ramp up the real project with confidence in your taskers' abilities. See https://scale-api.quip.com/C46CAkggaIo1/Prebuilding-Supply-with-Benchmarks for more information. -- Thanks Mahir J. and Jamie B. for contributing the definitions and advice for this section! ",
    children: pathsToChildren(
      ['prebuildAttempts', 'prebuildReviews', 'prebuildUseAutoPromotion', 'prebuildExpiresAt'],
      config,
    ),
  };
  allSections.push(prebuild);

  const taskHolding: ISection = {
    title: 'Task holding',
    parent: 'Task Holding',
    description:
      "The task holding section contain all configs related to purposefully controlling the state of a subtask, e.g. holding deliverable subtasks, throttling subtasks, etc. More configs will fill this section eventually. :')",
    children: pathsToChildren(
      [
        'holdDeliverableTasks',
        'smartSampleAttempt',
        'smartSampleL0',
        'smartSampleL1',
        'smartSampleL10',
      ],
      config,
    ),
  };
  allSections.push(taskHolding);

  const claimConfigs: ISection = {
    title: 'Claim time and Reclaim',
    parent: 'Claim',
    description:
      'This section with configs that dictate the max amount of time a worker has before losing their claim on a task. -- Thanks Sam C. for contributing the definitions and advice!',
    children: pathsToChildren(
      [
        ['zeroWorkReclaimDisabled'],
        [
          'claimTimeMaxIdleMinutesAttempt',
          'claimTimeMaxIdleMinutesL0',
          'claimTimeMaxIdleMinutesL1',
          'claimTimeMaxIdleMinutesReviewConsensus',
          'claimTimeMaxIdleMinutesL8',
          'claimTimeMaxIdleMinutesL10',
        ],
        [
          'claimTimeMaxHoldMinutesAttempt',
          'claimTimeMaxHoldMinutesL0',
          'claimTimeMaxHoldMinutesL1',
          'claimTimeMaxHoldMinutesReviewConsensus',
          'claimTimeMaxHoldMinutesL8',
          'claimTimeMaxHoldMinutesL10',
        ],
      ],
      config,
    ),
  };
  allSections.push(claimConfigs);

  const benchmarkThrottle: ISection = {
    title: 'Benchmark Throttle',
    parent: 'Benchmark Throttle',
    description: 'Throttles to prevent users from exeeding number of benchmark completions.',
    children: pathsToChildren(['throttleWorkerBenchmarkAttempt'], config),
  };

  allSections.push(benchmarkThrottle);

  const autoremoveBadTasker: ISection = {
    title: 'Autoremove Bad Tasker',
    parent: 'Autoremove Bad Taskers',
    description: 'Autoremove bad taskers from one WT to another',
    children: pathsToChildren(['autoremoveBadTasker'], config),
  };

  allSections.push(autoremoveBadTasker);

  const taskRedo: ISection = {
    title: 'Task Redo',
    parent: 'Task Redo',
    description: 'Enable task redo',
    children: pathsToChildren(['customerCanRedoTasks', 'redoLevel'], config),
  };
  allSections.push(taskRedo);

  const superCollectorDataCollectionSpecific: ISection = {
    title: 'Super Collectors: Data Collection Specific',
    parent: 'Super Collectors',
    description: 'Worker emails of taskers who can create new households',
    children: pathsToChildren(['superCollectors'], config),
  };
  allSections.push(superCollectorDataCollectionSpecific);

  const templateAnnotations: ISection = {
    title: 'Template Annotations',
    parent: 'General',
    description:
      'Template annotations for defining common annotations and links to be reused. Currently supports box and point annotations. Message 2D if you have any questions',
    children: pathsToChildren(
      [
        'templateAnnotations',
        'customerViewTemplateAnnotations',
        'customerEditTemplateAnnotations',
        'taskerEditTemplateAnnotations',
      ],
      config,
    ),
  };
  allSections.push(templateAnnotations);

  const attempterQualityFirstQueueSBQ: ISection = {
    title: 'Quality First Queue: Automated Worker Actions For Attempters - SBQ Based',
    parent: 'Quality First Queue',
    description:
      'Determine how workers can automatically get promoted or disabled based on their SBQ rates. See confluence docs for details: go/automated-worker-actions',
    children: pathsToChildren(
      [
        'attempterSBQRateThresholdDisable',
        'attempterSBQRateThresholdPromote',
        'attempterSBQRateTaskCount',
      ],
      config,
    ),
  };
  allSections.push(attempterQualityFirstQueueSBQ);

  const reviewerQualityFirstQueueSBQ: ISection = {
    title: 'Quality First Queue: Automated Worker Actions For Reviewers - SBQ Based',
    parent: 'Quality First Queue',
    description:
      'Determine how workers can automatically get promoted, demoted or disabled based on their SBQ rates.  See confluence docs for details: go/automated-worker-actions',
    children: pathsToChildren(
      [
        'reviewerSBQRateThresholdDisable',
        'reviewerSBQRateThresholdDemote',
        'reviewerSBQRateThresholdPromote',
        'reviewerSBQRateTaskCount',
      ],
      config,
    ),
  };
  allSections.push(reviewerQualityFirstQueueSBQ);

  return allSections;
};
